import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, TrendingUp, BookOpen, AlertCircle, Link2, Copy, Check, Share2, TrendingDown, Minus } from 'lucide-react'
import { api } from '../services/api'

interface Child {
  userId: string
  name: string
  email: string
  grade: string
  status: 'improving' | 'needs_support' | 'stable' | 'neutral'
  materialsThisWeek: number
  lastQuizScore: number
  overallProgress: number
  lastActiveAt: string | null
}

// ---------- helpers ----------

// Extract 1-2 letter initials. Prefers name, falls back to email prefix, then '?'.
function getInitials(name: string | undefined, email: string | undefined): string {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/)
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  if (email) return email.slice(0, 2).toUpperCase()
  return '?'
}

// Deterministic color from a fixed palette, keyed by a seed string.
// Keeps avatar color stable across renders without needing server state.
const AVATAR_PALETTE = ['#818CF8', '#6EE7B7', '#FCA5A1', '#F9A8D4', '#7DD3FC', '#BEF264', '#FDE68A', '#F0ABFC']
function avatarColorFor(seed: string): string {
  let hash = 0
  for (let i = 0; i < seed.length; i++) hash = (Math.imul(hash, 31) + seed.charCodeAt(i)) | 0
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length]
}

// Compact relative-time label in Spanish ("hace 3 min", "hace 2 d", "hace 1 mes").
function formatRelative(iso: string | null): string {
  if (!iso) return '—'
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return '—'
  const diff = Date.now() - t
  if (diff < 0) return 'ahora'
  const min = Math.floor(diff / 60_000)
  if (min < 1) return 'ahora'
  if (min < 60) return `hace ${min} min`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `hace ${hr}h`
  const d = Math.floor(hr / 24)
  if (d < 30) return `hace ${d}d`
  const mo = Math.floor(d / 30)
  if (mo < 12) return `hace ${mo}mes${mo > 1 ? 'es' : ''}`
  const yr = Math.floor(mo / 12)
  return `hace ${yr}año${yr > 1 ? 's' : ''}`
}

// ---------- presentational ----------

function Avatar({ name, email, size = 40 }: { name: string; email: string; size?: number }) {
  const seed = email || name || 'x'
  const bg = avatarColorFor(seed)
  const initials = getInitials(name, email)
  return (
    <div
      className="flex items-center justify-center flex-shrink-0"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${bg}, ${bg}cc)`,
        color: '#0A0F1E',
        fontWeight: 700,
        fontSize: size * 0.38,
        letterSpacing: 0.5,
        boxShadow: `0 0 0 1px rgba(255,255,255,0.08), 0 2px 8px ${bg}33`,
      }}
      aria-hidden
    >
      {initials}
    </div>
  )
}

function StatusChip({ status }: { status: Child['status'] }) {
  // NOTE: "stable" is *informational*, not a warning — so we use neutral slate instead
  // of amber to avoid contradicting a low ProgressBar. Only improving/needs_support carry
  // a semantic color.
  const config = {
    improving:     { label: 'Mejorando',      color: '#10B981', bg: 'rgba(16,185,129,0.1)',  icon: TrendingUp },
    needs_support: { label: 'Necesita apoyo', color: '#EF4444', bg: 'rgba(239,68,68,0.1)',   icon: TrendingDown },
    stable:        { label: 'Estable',        color: '#94A3B8', bg: 'rgba(148,163,184,0.1)', icon: Minus },
    neutral:       { label: 'Sin datos',      color: '#64748B', bg: 'rgba(100,116,139,0.1)', icon: Minus },
  }
  const { label, color, bg, icon: Icon } = config[status]
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium"
      style={{ color, background: bg }}
    >
      <Icon className="w-3 h-3" />
      {label}
    </span>
  )
}

function ProgressBar({ value }: { value: number }) {
  const color = value >= 80 ? '#10B981' : value >= 60 ? '#A3E635' : value >= 40 ? '#FBBF24' : value > 0 ? '#EF4444' : '#475569'
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
        <div
          className="h-1.5 rounded-full transition-all"
          style={{ width: `${Math.min(100, value)}%`, background: color }}
        />
      </div>
      <span className="text-sm font-semibold tabular-nums" style={{ color }}>
        {value > 0 ? `${value}%` : '—'}
      </span>
    </div>
  )
}

export default function ParentDashboardPage() {
  const navigate = useNavigate()
  const [children, setChildren] = useState<Child[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Invite code state
  const [code, setCode] = useState<string | null>(null)
  const [deepLink, setDeepLink] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const [copied, setCopied] = useState(false)
  const [copiedLink, setCopiedLink] = useState(false)

  useEffect(() => {
    api.getChildren()
      .then((data) => setChildren(data.children))
      .catch(() => setError('No se pudieron cargar los datos de tus hijos.'))
      .finally(() => setLoading(false))
  }, [])

  const avgOverall =
    children.length > 0
      ? Math.round(children.reduce((sum, c) => sum + c.overallProgress, 0) / children.length)
      : 0

  const totalMaterials = children.reduce((sum, c) => sum + c.materialsThisWeek, 0)

  const handleGenerate = async () => {
    setGenerating(true)
    try {
      const data = await api.generateLinkCode()
      setCode(data.code)
      setDeepLink(data.deep_link)
    } catch {
      alert('No se pudo generar el código. Intenta de nuevo.')
    } finally {
      setGenerating(false)
    }
  }

  const handleCopyCode = () => {
    if (!code) return
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleCopyLink = () => {
    if (!deepLink) return
    navigator.clipboard.writeText(deepLink)
    setCopiedLink(true)
    setTimeout(() => setCopiedLink(false), 2000)
  }

  const handleShareWhatsApp = () => {
    if (!deepLink) return
    const text = encodeURIComponent(`Entrá a Kapri con este link: ${deepLink}`)
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold" style={{ color: '#F1F5F9' }}>Panel de seguimiento</h1>
        <p className="text-sm mt-1" style={{ color: '#64748B' }}>
          Monitorea el progreso de tus hijos
        </p>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="glass-card p-5">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-4 h-4" style={{ color: '#818CF8' }} />
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#64748B' }}>
              Hijos vinculados
            </span>
          </div>
          <p className="text-3xl font-bold" style={{ color: '#F1F5F9' }}>{children.length}</p>
          <p className="text-xs mt-1" style={{ color: '#475569' }}>de 5 disponibles</p>
        </div>
        <div className="glass-card p-5">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-4 h-4" style={{ color: '#10B981' }} />
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#64748B' }}>
              Promedio global
            </span>
          </div>
          <p
            className="text-3xl font-bold"
            style={{
              color:
                avgOverall >= 80 ? '#10B981' :
                avgOverall >= 60 ? '#A3E635' :
                avgOverall >= 40 ? '#FBBF24' :
                avgOverall > 0  ? '#EF4444' : '#F1F5F9',
            }}
          >
            {avgOverall > 0 ? `${avgOverall}%` : '—'}
          </p>
        </div>
        <div className="glass-card p-5">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="w-4 h-4" style={{ color: '#FBBF24' }} />
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#64748B' }}>
              Materiales esta semana
            </span>
          </div>
          <p className="text-3xl font-bold" style={{ color: '#F1F5F9' }}>{totalMaterials}</p>
        </div>
      </div>

      {/* Children table */}
      <div className="glass-card overflow-hidden mb-6">
        <div className="px-6 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <h2 className="font-semibold" style={{ color: '#E2E8F0' }}>Mis hijos</h2>
        </div>

        {loading && (
          // Skeleton rows instead of bare "Cargando…"
          <div>
            {[0, 1].map((i) => (
              <div
                key={i}
                className="px-6 py-4 flex items-center gap-4"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
              >
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
                <div className="flex-1 flex flex-col gap-2">
                  <div style={{ width: 160, height: 12, borderRadius: 4, background: 'rgba(255,255,255,0.06)' }} />
                  <div style={{ width: 220, height: 10, borderRadius: 4, background: 'rgba(255,255,255,0.04)' }} />
                </div>
                <div style={{ width: 96, height: 14, borderRadius: 4, background: 'rgba(255,255,255,0.06)' }} />
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="p-6 flex items-center gap-3">
            <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#EF4444' }} />
            <p className="text-sm" style={{ color: '#94A3B8' }}>{error}</p>
          </div>
        )}

        {!loading && !error && children.length === 0 && (
          <div className="p-10 text-center">
            <Users className="w-10 h-10 mx-auto mb-3" style={{ color: '#334155' }} />
            <p className="text-sm font-medium" style={{ color: '#64748B' }}>
              No tienes hijos vinculados aún
            </p>
            <p className="text-xs mt-1" style={{ color: '#475569' }}>
              Genera un código de invitación para vincular a tu hijo
            </p>
          </div>
        )}

        {!loading && children.length > 0 && (
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {['Hijo/a', 'Progreso', 'Materiales', 'Última actividad', 'Estado', ''].map((h) => (
                  <th
                    key={h}
                    className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide"
                    style={{ color: '#475569' }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {children.map((c) => (
                <tr
                  key={c.userId}
                  className="transition-colors cursor-pointer"
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  onClick={() => navigate(`/parent/students/${c.userId}`)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <Avatar name={c.name} email={c.email} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: '#E2E8F0' }}>
                          {c.name || '—'}
                        </p>
                        <p
                          className="truncate"
                          style={{ color: '#475569', fontSize: 11, lineHeight: 1.3, marginTop: 1 }}
                          title={c.email}
                        >
                          {c.email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <ProgressBar value={c.overallProgress} />
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm" style={{ color: '#CBD5E1' }}>
                      {c.materialsThisWeek} <span style={{ color: '#64748B' }}>esta semana</span>
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className="text-xs tabular-nums"
                      style={{ color: c.lastActiveAt ? '#94A3B8' : '#475569' }}
                    >
                      {formatRelative(c.lastActiveAt)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <StatusChip status={c.status} />
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/parent/students/${c.userId}`) }}
                      className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                      style={{ color: '#818CF8', background: 'rgba(79,70,229,0.1)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(79,70,229,0.2)' }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(79,70,229,0.1)' }}
                    >
                      Ver detalle
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Invite code section */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-3 mb-5">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'rgba(79,70,229,0.12)', border: '1px solid rgba(79,70,229,0.2)' }}
          >
            <Link2 className="w-5 h-5" style={{ color: '#818CF8' }} />
          </div>
          <div>
            <h2 className="font-semibold" style={{ color: '#E2E8F0' }}>Código de invitación</h2>
            <p className="text-xs" style={{ color: '#64748B' }}>El link expira en 24 horas</p>
          </div>
        </div>

        {code ? (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: '#475569' }}>
              Código generado
            </p>
            <div
              className="flex items-center justify-between rounded-xl px-4 py-3 mb-4"
              style={{ background: 'rgba(79,70,229,0.08)', border: '1px solid rgba(79,70,229,0.2)' }}
            >
              <span
                className="text-2xl font-bold tracking-widest"
                style={{ color: '#A5B4FC', fontFamily: 'monospace' }}
              >
                {code}
              </span>
              <button
                onClick={handleCopyCode}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors"
                style={{ color: copied ? '#10B981' : '#818CF8', background: 'rgba(255,255,255,0.06)' }}
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copiado' : 'Copiar código'}
              </button>
            </div>
            <div className="flex gap-2 mb-4">
              <button
                onClick={handleShareWhatsApp}
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium"
                style={{
                  background: 'rgba(37,211,102,0.12)',
                  border: '1px solid rgba(37,211,102,0.3)',
                  color: '#4ADE80',
                }}
              >
                <Share2 className="w-4 h-4" />
                Compartir por WhatsApp
              </button>
              <button
                onClick={handleCopyLink}
                className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-xl transition-colors"
                style={{ color: copiedLink ? '#10B981' : '#818CF8', background: 'rgba(255,255,255,0.06)' }}
              >
                {copiedLink ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                {copiedLink ? 'Copiado' : 'Copiar link'}
              </button>
            </div>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="btn-secondary text-sm disabled:opacity-60"
            >
              Generar nuevo código
            </button>
          </div>
        ) : (
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="btn-primary flex items-center gap-2 disabled:opacity-60"
          >
            <Link2 className="w-4 h-4" />
            {generating ? 'Generando...' : 'Generar código de invitación'}
          </button>
        )}
      </div>
    </div>
  )
}
